// The seller's return address while answering a return request: shown as the
// buyer will get it, or — none yet — added on the spot ("Add now" opens a small
// dialog of its own above the answer), so the answer goes on from where it was.
// Saving goes through `api.setReturnAddress(order_id, address, order)`: the
// project's own way of keeping it (aldoo.com: the company's Delivery settings).
import React, { useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import { Z } from "aldoo-ra/ZLayers"
import { showNotification } from "aldoo-ra/Notification"
import { errorMessage } from "./ui"

const PLACEHOLDER = "Company name\nWarehouse or office\nStreet and number\nPostcode City, Country"

/** A dialog to write the return address in; `onSaved(address)` once it is kept. */
export function ReturnAddressDialog({ isOpen, onClose, onSaved, order, api, t, initial = "" }) {
  const [address, setAddress] = useState(initial)
  const [saving, setSaving] = useState(false)
  const save = async () => {
    const value = address.trim()
    if (!value) return showNotification({ message: t`Enter the return address`, className: "bg-admin_error text-white" })
    setSaving(true)
    try {
      const res = await api.setReturnAddress(order?.orderId || order?._id, value, order)
      const message = errorMessage(res)
      if (message) return showNotification({ message, className: "bg-admin_error text-white" })
      showNotification({ message: t`Return address saved`, className: "bg-admin_success text-white" })
      onSaved?.(value)
      onClose?.()
    } finally {
      setSaving(false)
    }
  }
  return (
    // above the answer's own dialog (the system layer), which stays open under it
    <Modal isOpen={isOpen} onClose={onClose} zIndex={Z.system + 1} title={t`Physical items return address`} className="max-w-[520px]">
      <div className="flex flex-col gap-3 p-1" data-testid="return-address-dialog">
        <span className="text-sm text-admin_text_secondary">{t`Where buyers send items back. Written as it goes on the parcel. It is kept in your Delivery settings.`}</span>
        <textarea
          autoFocus
          rows={5}
          maxLength={1000}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder={t(PLACEHOLDER)}
          className="px-3 py-2 rounded-md border text-sm outline-none resize-y border-admin_bordered bg-admin_paper text-admin_text"
          data-testid="return-address-input"
        />
        <div className="flex justify-end gap-2">
          <Button variant="admin_secondary" onClick={onClose} disabled={saving}>{t`Cancel`}</Button>
          <Button variant="admin_primary" onClick={save} disabled={saving} data-testid="return-address-save">{t`Update`}</Button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * The return address in the accept dialog: what the buyer gets, or the notice
 * that there is none with "Add now". `onChange(address)` tells the dialog it may
 * now send.
 */
export function AcceptReturnAddress({ order, api, t, initial = "", onChange }) {
  const [address, setAddress] = useState(initial)
  const [adding, setAdding] = useState(false)
  const dialog = adding && (
    <ReturnAddressDialog
      isOpen
      order={order}
      api={api}
      t={t}
      initial={address}
      onClose={() => setAdding(false)}
      onSaved={(value) => {
        setAddress(value)
        onChange?.(value)
      }}
    />
  )
  if (address)
    return (
      <div className="rounded-md bg-black/[0.04] px-3 py-2 text-sm flex items-start gap-3" data-testid="request-return-address">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-admin_text_secondary mb-0.5">{t`The buyer is sent this return address:`}</div>
          <div className="whitespace-pre-line">{address}</div>
        </div>
        {/* a mistake in it is put right here, in the company's Delivery settings too */}
        {api?.setReturnAddress && (
          <Button variant="admin_secondary" className="min-w-0 shrink-0" onClick={() => setAdding(true)} data-testid="return-address-edit">
            {t`Edit`}
          </Button>
        )}
        {dialog}
      </div>
    )
  return (
    <div className="rounded-md bg-admin_error/10 px-3 py-2 text-sm text-admin_error flex flex-col gap-2" data-testid="request-return-address-missing">
      <span>{t`You have no return address yet. Add your physical items return address in your Delivery settings first: the buyer needs it to send the items back.`}</span>
      {api?.setReturnAddress && (
        <Button variant="admin_primary" className="self-start min-w-0" onClick={() => setAdding(true)} data-testid="return-address-add">
          {t`Add now`}
        </Button>
      )}
      {dialog}
    </div>
  )
}
